<script>
    import debounce from "lodash/debounce";

    import { apiMethods } from "../../../Services/index.svelte";

    import ErrorModal from "../../../Components/Common/ErrorModal.svelte";

    import { BillingType } from "../../../Utils/utilEnums.svelte";

    import {
        getBillingType,
        setInputFilter,
        showError,
        showException,
    } from "../../../Utils/utilFunctions.svelte";

    import { SEARCH } from "../../../Utils/utilConstants.svelte";

    import { user, createUser } from "../../../stores";
    import Loader from "../../../Components/Common/Loader.svelte";

    export let personalDetails = {};
    export let updatePhase = () => {};

    let billingRuleObj = { isDefault: true, accountId: personalDetails.id },
        isAddingRule = false,
        isCreatingAccount = false,
        isBindingSignature = false,
        isSendingSignatureForm = false,
        isAddingBillingRule = false,
        showFinishModal = false;

    let amountSelectorMin = 0,
        amountSelectorMax = 0;

    $: if (billingRuleObj.type)
        setInputFilter(
            document.getElementById("billingAmountInput"),
            function (value) {
                return value ? /^\d*\.?\d*$/.test(value) : true;
            },
            "Invalid input",
        );

    const onBoardingProcess = async () => {
        if (!_checkFieldsValidation()) return;

        isAddingRule = true;

        await createAccount();

        if (personalDetails.id) await bindSignatureToAccount();

        if (personalDetails.signatureApproved) await addBillingRule();

        isAddingRule = false;
    };

    const createAccount = async () => {
        isCreatingAccount = true;

        await apiMethods.postMethod
            .createAccount(personalDetails)
            .then((res) => _handleCreateAccountRes(res));

        isCreatingAccount = false;
    };

    const bindSignatureToAccount = async () => {
        const data = {
            type: 1,
            accountId: personalDetails.id,
            content: personalDetails.signatureContent,
        };

        isBindingSignature = true;

        await apiMethods.postMethod
            .sendAccountSignature(data)
            .then(async (res) => await _handleBindSignatureRes(res));

        isBindingSignature = false;
    };

    const addBillingRule = async () => {
        isAddingBillingRule = true;

        await apiMethods.postMethod
            .addBillingRule(billingRuleObj)
            .then((res) => {
                if (!res.id)
                    showError(
                        "Failed to add billing rule, please contact support",
                    );
                else personalDetails.billingRuleAdded = true;
            })
            .catch((err) =>
                showException(
                    "Failed to add billing rule, please contact support",
                ),
            );

        isAddingBillingRule = false;

        showFinishModal = true;
    };

    // HELPER FUNCTIONS
    const _checkFieldsValidation = () => {
        _updateInvalidFieldsStyle();

        return billingRuleObj.name && billingRuleObj.type;
    };

    const _updateInvalidFieldsStyle = () => {
        if (!billingRuleObj.name) _setErrorStyle("billingRuleNameInput");
        else _resetDefaultStyle("billingRuleNameInput");

        if (!billingRuleObj.type) _setErrorStyle("billingRuleTypeSelect");
        else _resetDefaultStyle("billingRuleTypeSelect");
    };

    const _updateAmountSelectorProperties = () => {
        switch (billingRuleObj.type) {
            case 2:
                amountSelectorMin = 40;
                amountSelectorMax = 94;
                break;
            case 3:
                amountSelectorMin = 0;
                amountSelectorMax = 500;
                break;
            case 4:
                amountSelectorMin = 200;
                amountSelectorMax = 1500;
                break;
            case 5:
                amountSelectorMin = 100;
                amountSelectorMax = 2000;
                break;
            case 6:
                amountSelectorMin = 100;
                amountSelectorMax = 450;
                break;
            case 7:
                amountSelectorMin = 100;
                amountSelectorMax = 200;
                break;
            default:
                amountSelectorMin = 0;
                amountSelectorMax = 0;
                break;
        }

        billingRuleObj.amount = amountSelectorMin;
    };

    const _updateBillingAmount = (e) => {
        // remove auto-adjustment for amount in [amountSelectorMin, amountSelectorMax] range
        // if (billingRuleObj.amount > amountSelectorMax)
        //     billingRuleObj.amount = amountSelectorMax;

        // if (billingRuleObj.amount < amountSelectorMin)
        //     billingRuleObj.amount = amountSelectorMin;

        billingRuleObj.amount = Number(billingRuleObj.amount);
    };

    const _setErrorStyle = (id) => {
        const field = document.getElementById(`${id}`);
        field.style.outline = "red solid 2px";
    };

    const _resetDefaultStyle = (id) => {
        const field = document.getElementById(`${id}`);
        field.style.outline = "none";
    };

    const _handleCreateAccountRes = async (res) => {
        if (res.error) showError(res.error);
        else if (!res.id)
            showError(
                "Failed to create account, please refresh the page and try again and contact support if the issue persists.",
            );
        else {
            personalDetails = {
                ...res,
                signatureContent: personalDetails.signatureContent,
            };
            billingRuleObj.accountId = res.id;

            await apiMethods.getMethod.getUserById(
                personalDetails.createdUserId,
            );
        }
    };

    const _handleBindSignatureRes = async (res) => {
        if (res.id) {
            const data = {
                accountId: personalDetails.id,
            };

            personalDetails.signatureSent = true;

            isSendingSignatureForm = true;

            await apiMethods.postMethod
                .sendAccountOnboardingForm(data)
                .then((res) => {
                    if (res.toString() == "true")
                        personalDetails.signatureApproved = true;
                });

            isSendingSignatureForm = false;
        } else
            showError(
                "Failed to upload signature, please refresh the page and try again and contact support if the issue persists.",
            );
    };
</script>

<form
    class="flex flex-col gap-y-4 w-full p-6"
    on:submit|preventDefault={onBoardingProcess}
>
    <div class="flex flex-col gap-y-2">
        <label
            for="billingRuleName"
            class="mandatory leading-7 text-sm text-gray-600 font-bold text-[16px]"
        >
            Name
        </label>

        <input
            type="text"
            class="input input-sm"
            name="billingRuleName"
            id="billingRuleNameInput"
            autocomplete="off"
            bind:value={billingRuleObj.name}
            on:focus={() => _resetDefaultStyle("billingRuleNameInput")}
        />
    </div>

    <div class="flex flex-col gap-y-2">
        <label
            for="billingRuleType"
            class="mandatory leading-7 text-sm text-gray-600 font-bold text-[16px]"
        >
            Type of billing rule
        </label>

        <select
            class="select select-sm"
            name="billingRuleType"
            id="billingRuleTypeSelect"
            bind:value={billingRuleObj.type}
            on:focus={() => _resetDefaultStyle("billingRuleTypeSelect")}
            on:change={_updateAmountSelectorProperties}
        >
            <option value="" />
            {#each BillingType as type}
                {#if type.id > 1}
                    <option value={type.id}>
                        {type.type.toString().replace("Set", "")}
                    </option>
                {/if}
            {/each}
        </select>
    </div>

    {#if billingRuleObj.type}
        <div class="flex flex-col gap-y-2">
            <label
                for="billingAmount"
                class="leading-7 text-sm text-gray-600 font-bold text-[16px]"
            >
                {getBillingType(billingRuleObj.type)}
            </label>

            <input
                type="range"
                min={amountSelectorMin}
                max={amountSelectorMax}
                class="range range-xs range-secondary"
                bind:value={billingRuleObj.amount}
            />

            <input
                type="number"
                class="input input-sm"
                autocomplete="off"
                id="billingAmountInput"
                bind:value={billingRuleObj.amount}
                on:input={debounce(
                    () => _updateBillingAmount(),
                    SEARCH.DEBOUNCE_TIME,
                )}
            />
        </div>
    {/if}

    <div class="flex w-full justify-around items-center">
        <p class="text-[16px]">Charge patient</p>

        <div class="flex gap-x-4">
            <button
                class={`btn btn-md rounded-full shadow-2xl border border-2 ${
                    billingRuleObj.hasOwnProperty("oopType")
                        ? billingRuleObj.oopType == 0
                            ? "bg-gray-600 text-[#fff]"
                            : "bg-[#fff] text-gray-600"
                        : ""
                }`}
                on:click|preventDefault={() => (billingRuleObj.oopType = 0)}
            >
                Before
            </button>
            <button
                class={`btn btn-md rounded-full shadow-2xl border border-2 ${
                    billingRuleObj.hasOwnProperty("oopType")
                        ? billingRuleObj.oopType == 1
                            ? "bg-gray-600 text-[#fff]"
                            : "bg-[#fff] text-gray-600"
                        : ""
                }`}
                on:click|preventDefault={() => (billingRuleObj.oopType = 1)}
            >
                After
            </button>
        </div>
    </div>

    <button
        class={`btn btn-md btn-secondary w-full text-[#fff] ${
            isAddingRule ? "loading" : ""
        }`}
        on:click|preventDefault={onBoardingProcess}
        disabled={isAddingRule}
    >
        {isAddingRule ? "Adding" : "Add rule"}
    </button>
</form>

<div
    class={isAddingRule || personalDetails.billingRuleAdded
        ? "modal modal-open sm:modal-middle"
        : "modal"}
>
    <div class="modal-box">
        <h3 class="font-medium text-lg">
            {isAddingRule ? "Uploading your details..." : "Done"}
        </h3>
        <div class="flex flex-col gap-y-4 p-4">
            <div class="flex items-center justify-between">
                <p>
                    {isCreatingAccount ? "Creating Account" : "Account created"}
                </p>
                <div>
                    {#if isCreatingAccount}
                        <Loader />
                    {:else if personalDetails.id}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="stroke-current flex-shrink-0 h-6 w-6 text-secondary"
                            fill="none"
                            viewBox="0 0 24 24"
                            ><path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            /></svg
                        >
                    {:else}
                        Pending
                    {/if}
                </div>
            </div>

            <div class="flex items-center justify-between">
                <p>
                    {isBindingSignature || !personalDetails.signatureSent
                        ? "Binding Signature"
                        : "Signature bind to account"}
                </p>
                <div>
                    {#if isBindingSignature && !personalDetails.signatureSent}
                        <Loader />
                    {:else if personalDetails.signatureSent}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="stroke-current flex-shrink-0 h-6 w-6 text-secondary"
                            fill="none"
                            viewBox="0 0 24 24"
                            ><path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            /></svg
                        >
                    {:else}
                        Pending
                    {/if}
                </div>
            </div>

            <div class="flex items-center justify-between">
                <p>
                    {isSendingSignatureForm
                        ? "Sending Signature Form"
                        : "Signature Form Sent"}
                </p>
                <div>
                    {#if isSendingSignatureForm}
                        <Loader />
                    {:else if personalDetails.signatureApproved}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="stroke-current flex-shrink-0 h-6 w-6 text-secondary"
                            fill="none"
                            viewBox="0 0 24 24"
                            ><path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            /></svg
                        >
                    {:else}
                        Pending
                    {/if}
                </div>
            </div>

            <div class="flex items-center justify-between">
                <p>
                    {isAddingBillingRule
                        ? "Adding Billing Rule"
                        : "Billing Rule Added"}
                </p>
                <div>
                    {#if isAddingBillingRule}
                        <Loader />
                    {:else if personalDetails.billingRuleAdded}
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="stroke-current flex-shrink-0 h-6 w-6 text-secondary"
                            fill="none"
                            viewBox="0 0 24 24"
                            ><path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            /></svg
                        >
                    {:else}
                        Pending
                    {/if}
                </div>
            </div>
        </div>
        {#if personalDetails.billingRuleAdded}
            <div class="modal-action">
                <!-- <button
                    class="btn btn-secondary text-white btn-sm border-none"
                    on:click={updatePhase(10)}
                >
                    Go to Dashboard
                </button> -->
                <button
                    class="btn btn-secondary text-white btn-sm border-none"
                    on:click={updatePhase(9)}
                >
                    Go to Survey
                </button>
            </div>
        {/if}
    </div>
</div>

<style>
    .mandatory::before {
        content: " * ";
    }
</style>
